import { useState, useRef, useEffect } from 'react';

/**
 * Type-to-filter box with a suggestion list — for narrowing a table column where
 * a plain <select> means hunting through a long list of values.
 *
 * Typing filters as you go (the caller decides how the text is matched against a
 * row), and the list below narrows to the values that are actually present, so a
 * value can still be picked exactly without knowing how it is spelled.
 *
 * Props:
 *   value       — current filter text ('' when the filter is off)
 *   onChange    — (text: string) => void
 *   options     — distinct suggestion labels to offer
 *   placeholder — string, names the column being filtered
 *   width       — px width of the input (default 150)
 */
export default function FilterCombo({ value = '', onChange, options = [], placeholder, width = 150 }) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const ref = useRef(null);

  const typed = value.trim().toLowerCase();
  const matches = typed ? options.filter(o => o.toLowerCase().includes(typed)) : options;

  useEffect(() => {
    const handle = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // Keep the highlight on a row that still exists as the list narrows.
  useEffect(() => { setHighlight(0); }, [value]);

  const pick = (option) => { onChange(option); setOpen(false); };

  const onKeyDown = (e) => {
    if (e.key === 'Escape')        { setOpen(false); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHighlight(i => Math.min(i + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlight(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && open && matches[highlight]) { e.preventDefault(); pick(matches[highlight]); }
  };

  return (
    <div ref={ref} className="relative" style={{ width }}>
      <input
        className="form-input text-[9px] py-1 pr-5"
        value={value}
        placeholder={placeholder}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {value && (
        <button
          type="button"
          title="Clear this filter"
          onMouseDown={e => { e.preventDefault(); onChange(''); }}
          className="absolute right-1 top-1/2 -translate-y-1/2 leading-none text-[10px] text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
      )}
      {open && (
        <div className="absolute z-50 left-0 min-w-full mt-0.5 border border-gray-200 rounded bg-white shadow-lg max-h-[220px] overflow-y-auto whitespace-nowrap">
          {matches.length === 0 ? (
            <div className="px-2 py-1.5 text-[9px] text-gray-400 italic">No match for &ldquo;{value.trim()}&rdquo;</div>
          ) : matches.map((option, i) => (
            <div
              key={option}
              onMouseDown={e => { e.preventDefault(); pick(option); }}
              onMouseEnter={() => setHighlight(i)}
              className={`px-2 py-1 text-[9px] cursor-pointer border-b border-gray-50 last:border-0 ${i === highlight ? 'bg-gold/10' : 'hover:bg-gold/5'}`}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
